import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { APIService } from 'src/app/shared/service/apiService.service';

interface UserRow {
  id: number;
  displayName: string;
  email: string;
  phoneNumber: string;
  roles: string;
  active: boolean | number;
  created_at: string;
}

@Component({
  selector: 'app-user-query',
  templateUrl: './user-query.component.html',
  styleUrls: ['./user-query.component.scss'],
})
export class UserQueryComponent implements OnInit {
  users: UserRow[] = [];
  loading = false;
  totalCount = 0;
  totalPages = 0;
  currentPage = 0;
  readonly limit = 20;

  search = '';
  roleFilter = '';
  activeFilter = '';

  readonly roleOptions = [
    { value: '',          label: 'Todos los perfiles' },
    { value: 'student',   label: 'Estudiante' },
    { value: 'teacher',   label: 'Instructor' },
    { value: 'business',  label: 'Empresa' },
    { value: 'admin',     label: 'Administrador' },
  ];

  readonly roleCreateOptions = [
    { value: 'student',  label: 'Estudiante' },
    { value: 'teacher',  label: 'Instructor' },
    { value: 'business', label: 'Empresa' },
    { value: 'admin',    label: 'Administrador' },
  ];

  readonly activeOptions = [
    { value: '',  label: 'Todos los estados' },
    { value: '1', label: 'Activa' },
    { value: '0', label: 'Inactiva' },
  ];

  userForm: FormGroup;
  isSubmitting = false;

  constructor(
    private apiService: APIService,
    private modalService: NgbModal,
    private fb: FormBuilder,
  ) {
    this.userForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName:  ['', [Validators.required, Validators.minLength(2)]],
      email:     ['', [Validators.required, Validators.email]],
      roles:     ['student', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.loading = true;
    this.apiService.getUsersQuery({
      q:      this.search || undefined,
      role:   this.roleFilter || undefined,
      active: this.activeFilter,
      limit:  this.limit,
      offset: this.currentPage,
    }).subscribe({
      next: (res) => {
        this.users      = res.List;
        this.totalCount = res.Count;
        this.totalPages = res.Pages;
        this.loading    = false;
      },
      error: () => { this.loading = false; },
    });
  }

  onSearch(): void {
    this.currentPage = 0;
    this.loadUsers();
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadUsers();
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadUsers();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadUsers();
    }
  }

  openNewUserModal(content: any): void {
    this.userForm.reset({ roles: 'student' });
    this.modalService.open(content, { size: 'md', backdrop: 'static', keyboard: false });
  }

  closeModal(): void {
    this.modalService.dismissAll();
    this.userForm.reset({ roles: 'student' });
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const { firstName, lastName, email, roles } = this.userForm.value;

    this.apiService.adminCreateUser({ firstName, lastName, email, roles }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.closeModal();
        Swal.fire({
          icon: 'success',
          title: 'Usuario creado',
          text: 'Se envió un correo al usuario para que active su cuenta y establezca su contraseña.',
          confirmButtonColor: '#007bff',
          confirmButtonText: 'Entendido',
        });
        this.loadUsers();
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg = err?.error?.message || 'Ocurrió un error al crear el usuario.';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg,
          confirmButtonColor: '#dc3545',
        });
      },
    });
  }

  roleLabel(role: string): string {
    const found = this.roleOptions.find(o => o.value === role);
    return found ? found.label : role;
  }

  roleBadgeClass(role: string): string {
    const map: Record<string, string> = {
      student:  'badge-primary',
      teacher:  'badge-info',
      business: 'badge-warning',
      admin:    'badge-danger',
    };
    return `badge ${map[role] || 'badge-secondary'}`;
  }

  get f() { return this.userForm.controls; }
}
